import { formatDate } from '@frontend/mwm-ui'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import { isArray } from 'highcharts'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MdOutlineClear, MdOutlineFlag } from 'react-icons/md'
import { useLocation } from 'react-router-dom'

import type { ReactElement, SyntheticEvent } from 'react'
import type { IStateReducer, TFilterKey } from '~/types/analytics-context'
import type { IAppSummary } from '~/types/application'
import type { IFilterPreset } from '~/types/filter-preset'

import NewPresetModal from './NewPresetModal'

import { getLookersFilters } from '~/api/lookers'
import Button from '~/components/Buttons/Button'
import CollapseButton from '~/components/Buttons/CollapseButton'
import DeprecationBanner from '~/components/DeprecationBanner'
import Filters, { FiltersContent } from '~/components/Filters'
import DateInput from '~/components/Inputs/Basic/DateInput'
import MultiSelectInput from '~/components/Inputs/Basic/MultiSelectInput'
import SelectInput from '~/components/Inputs/Basic/SelectInput'
import ModalDialog from '~/components/ModalDialog'
import CustomAppOption, { CustomAppControl } from '~/components/SelectOptions/CustomAppOption'
import TooltipMenu from '~/components/TooltipMenu'
import { BR_MAX_DAYS_RANGE, LOCAL_STORAGE, MAX_DAYS_RANGE, SESSION_STORAGE, TODAY } from '~/const'
import { breakdownOptions, businessReportsBreakdownOptions } from '~/const/graphs-builder'
import { getDeviceTypeOptions } from '~/const/options'
import useApps from '~/hooks/useApps'
import { useOnClickOutside } from '~/hooks/useOnClickOutside'
import { useAnalyticsContext } from '~/providers/Analytics'
import { useAppSummary } from '~/providers/AppSummary'
import { useSearchParamsContext } from '~/providers/SearchParams'
import { isBusinessReportsFeature } from '~/utils/analytics-context'
import { semverComparator } from '~/utils/comparators'
import { getFullNameCountries } from '~/utils/countries'
import { startsWithFilter } from '~/utils/options'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const getOptionFromFilters = (filters: string[], options: IOption[]): IOption[] =>
  options.filter(({ value }) => filters.includes(value))

/** @param arr It comes from the backend, is typically string[] for all filters, and number[] for DAI */
const getOptions = (arr: unknown[], sort?: 'ASC' | 'DESC', isSemver?: boolean): { label: string; value: string }[] => {
  const options = arr.map((val) => ({ label: `${val}`, value: `${val}` }))

  if (isSemver) {
    const optionsSortedAsc = options.sort((a, b) => semverComparator(a.label, b.label))
    return sort === 'DESC' ? optionsSortedAsc.reverse() : optionsSortedAsc
  }

  return sort === 'ASC'
    ? options.sort((a, b) => a.label.localeCompare(b.label))
    : sort === 'DESC'
      ? options.sort((a, b) => b.label.localeCompare(a.label))
      : options
}

const getBreakdowns = (state: IStateReducer) => {
  const breakdowns = []
  if (state.filters.breakdown?.[0] === 'abtest') {
    breakdowns.push({ key: 'abtest_key', value: [] })
  } else if (state.filters.breakdown?.[0] === 'dsv3') {
    breakdowns.push({ key: 'placement_key', value: state.filters.placementKey || [] })
  }

  return breakdowns
}

export interface Props {
  app?: IAppSummary
  onWithEventsChange: (withEvents: boolean) => void
}

const AnalyticsFilters = ({ app, onWithEventsChange }: Props): ReactElement => {
  /** Local state */

  const { appKeys, isAppsLoading } = useApps()

  const appKeyOptions = appKeys.map(({ appKey, url }) => ({ label: appKey, url, value: appKey }))

  const location = useLocation()

  const withEventForSession = sessionStorage.getItem(SESSION_STORAGE.SHOW_EVENTS_ON_CHARTS)

  const { dispatchToSearchParams, searchParamsObject } = useSearchParamsContext()

  const { dispatch, state } = useAnalyticsContext()

  const tooltipMenuRef = useRef<HTMLMenuElement>(null)

  const { appKey, deviceType } = useAppSummary()

  const [currentApp, setCurrentApp] = useState({
    appKey: app ? app.app_key : appKey,
    deviceType: app ? app.device_type : deviceType,
  })

  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false)

  const [withEvents, setWithEvents] = useState(!!withEventForSession ? withEventForSession === 'true' : true)

  const [isPresetsOpen, setIsPresetsOpen] = useState(false)

  const [isPresetModalOpen, setIsPresetModalOpen] = useState(false)

  const [isDeletePresetModalOpen, setIsDeletePresetModalOpen] = useState(false)

  const [selectedPreset, setSelectedPreset] = useState<IFilterPreset | null>(null)

  const navigationGraphIdFromURL = searchParamsObject.navigationGraphId

  const filtersPresets: IFilterPreset[] = JSON.parse(localStorage.getItem(LOCAL_STORAGE.FILTERS_PRESETS) || '[]')

  const isBusinessReports = isBusinessReportsFeature(state.feature)

  const maxDateRange = useMemo(() => (isBusinessReports ? BR_MAX_DAYS_RANGE : MAX_DAYS_RANGE), [isBusinessReports])

  /** Queries & Mutations */

  const { data: filtersOptions, isLoading: isFiltersOptionsLoading } = useQuery<{
    data: { data: unknown[]; name: string }[] | null
  }>({
    enabled: !!currentApp.appKey && !!currentApp.deviceType,
    queryFn: () =>
      getLookersFilters(currentApp.appKey as string, currentApp.deviceType as string, {
        dateRange: state.computedFiltersDate.range,
        filters: getBreakdowns(state),
      }),
    queryKey: ['looker-filters', currentApp, getBreakdowns(state)],
  })

  /** Query dependent state */

  const genericGetOptions = useCallback(
    (filterName: string, sort?: 'ASC' | 'DESC', isSemver?: boolean) =>
      getOptions(filtersOptions?.data?.find(({ name }) => name === filterName)?.data || [], sort, isSemver),
    [filtersOptions?.data]
  )

  const daiOptions = useMemo(() => genericGetOptions('dai'), [genericGetOptions])
  const installationAppVersionOptions = useMemo(
    () => genericGetOptions('installation_app_version', 'DESC', true),
    [genericGetOptions]
  )
  const countryOptions = useMemo(() => {
    const options = genericGetOptions('installation_country', 'ASC')
      .map((option) => ({
        ...option,
        icon: <span className={classNames('option-flag-icon fi', `fi-${option.label.toLowerCase()}`)} />,
        label: option.value === 'ROTW' ? 'Rest of the World' : getFullNameCountries([option.value]).toString(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label))

    !isBusinessReports && options.unshift({ icon: <span>🌏</span>, label: 'Worldwide', value: 'WW' })

    return options
  }, [genericGetOptions, isBusinessReports])
  const deviceCategoryOptions = useMemo(() => genericGetOptions('device_category', 'ASC'), [genericGetOptions])
  const deviceTypeOptions = getDeviceTypeOptions(['android', 'ios'])
  const uaSourceOptions = useMemo(() => genericGetOptions('ua_source', 'ASC'), [genericGetOptions])
  const userTypeOptions = useMemo(() => genericGetOptions('user_type', 'ASC'), [genericGetOptions])
  const abtestKeyOptions = useMemo(() => genericGetOptions('abtest_key', 'ASC'), [genericGetOptions])
  const placementKeyOptions = useMemo(() => genericGetOptions('placement_key', 'ASC'), [genericGetOptions])
  const navigationGraphIdOptions = useMemo(() => genericGetOptions('navigation_graph_id'), [genericGetOptions])

  const refinedBreakdownOptions = useMemo(
    () =>
      isBusinessReports
        ? businessReportsBreakdownOptions
        : location.pathname.includes('app-live')
          ? breakdownOptions.filter((option) => option.value !== 'dsv3' && option.value !== 'user_type')
          : breakdownOptions,
    [isBusinessReports, location.pathname]
  )

  const setFilters = useCallback(
    (filters: Partial<Record<TFilterKey, string[]>>) => {
      dispatch({ payload: filters, type: 'SET_FILTERS' })
      dispatchToSearchParams({ type: 'update', value: filters })
    },
    [dispatch, dispatchToSearchParams]
  )

  /** Effects */

  useOnClickOutside(tooltipMenuRef, () => setIsPresetsOpen(false))

  useEffect(() => {
    if ((appKey && appKey !== currentApp.appKey) || (deviceType && deviceType !== currentApp.deviceType)) {
      dispatch({ type: 'RESET_FILTERS' })
      dispatchToSearchParams({ type: 'reset-ca-filters' })
      dispatchToSearchParams({ type: 'reset-business-reports-filters' })
      setCurrentApp({ appKey, deviceType })
    }
  }, [appKey, deviceType, currentApp, dispatch, dispatchToSearchParams])

  useEffect(() => {
    if (state.filters.breakdown?.[0] === 'dsv3' && !navigationGraphIdFromURL && navigationGraphIdOptions.length) {
      dispatch({
        payload: {
          navigationGraphId: navigationGraphIdOptions.map(({ value }) => value),
        },
        type: 'SET_FILTERS',
      })
    }
  }, [dispatch, navigationGraphIdFromURL, navigationGraphIdOptions, state.filters.breakdown])

  useEffect(() => {
    if (state.filters.breakdown?.[0] !== 'abtest') {
      dispatchToSearchParams({ type: 'update', value: { abtestKey: [] } })
    }
    if (state.filters.breakdown?.[0] !== 'dsv3') {
      dispatchToSearchParams({ type: 'update', value: { navigationGraphId: [], placementKey: [] } })
    }
  }, [dispatchToSearchParams, state.filters.breakdown])

  useEffect(() => {
    if (state.filters.breakdown?.[0] && state.filters.dai) {
      dispatchToSearchParams({ type: 'update', value: { dai: state.filters.dai } })
    }
  }, [dispatchToSearchParams, state.filters])

  useEffect(() => {
    if (state.filters.installationCountry?.length) {
      dispatchToSearchParams({
        type: 'update',
        value: { installationCountry: state.filters.installationCountry },
      })
    } else {
      setFilters({ installationCountry: ['WW'] })
      dispatchToSearchParams({
        type: 'update',
        value: { installationCountry: ['WW'] },
      })
    }
  }, [dispatchToSearchParams, setFilters, state.filters])

  /** Handlers */

  const toggleCollapseFilters = () => {
    setIsFiltersCollapsed(!isFiltersCollapsed)
    dispatch({ payload: !isFiltersCollapsed, type: 'SET_FILTERS_COLLAPSED' })
  }

  const handleFilterChanged = (filterName: TFilterKey, selectedOptions: IOption | IOption[] | null) => {
    if (selectedOptions === null) {
      setFilters({ [filterName]: [] })
    } else if (isArray(selectedOptions)) {
      const values = (selectedOptions as IOption[]).map((option) => option.value)

      if (filterName === 'installationCountry') {
        const lastValue = values[values.length - 1]
        setFilters({ [filterName]: lastValue === 'WW' ? ['WW'] : values.filter((el) => el !== 'WW') })
        return
      }

      setFilters({ [filterName]: values })
    } else {
      const filters = { [filterName]: [(selectedOptions as IOption).value] }

      setFilters(filters)
    }
  }

  const handleDatePickerChanged = (dateRange: string[]) => {
    const [startDate, endDate] = dateRange

    setFilters({
      endDate: [formatDate(endDate, 'YYYY-MM-DD')],
      startDate: [formatDate(startDate, 'YYYY-MM-DD')],
    })
  }

  const handleResetAll = () => {
    dispatch({ type: 'RESET_FILTERS' })
    dispatchToSearchParams({ type: 'reset-ca-filters' })
  }

  const handleWithEventsToggleClick = () => {
    setWithEvents((prev) => {
      sessionStorage.setItem(SESSION_STORAGE.SHOW_EVENTS_ON_CHARTS, `${!prev}`)
      onWithEventsChange(!prev)
      return !prev
    })
  }

  const handleOpenPresets = () => setIsPresetsOpen(true)

  const handleOpenPresetModal = (isOpen: boolean) => () => setIsPresetModalOpen(isOpen)

  const handleOpenDeletePresetModal =
    (isOpen: boolean, preset?: IFilterPreset) => (evt?: SyntheticEvent<HTMLDivElement>) => {
      evt?.stopPropagation()

      if (preset) {
        setSelectedPreset(preset)
      }

      setIsDeletePresetModalOpen(isOpen)
    }

  const handleSelectPreset = (preset: IFilterPreset) => () => {
    dispatch({ payload: preset.filters, type: 'SET_FILTERS' })
    dispatchToSearchParams({ type: 'update', value: preset.filters })
  }

  const handleDeletePreset = () => {
    if (selectedPreset) {
      const updatedPresets = filtersPresets.filter((preset) => preset.name !== selectedPreset.name)

      localStorage.setItem(LOCAL_STORAGE.FILTERS_PRESETS, JSON.stringify(updatedPresets))
      setIsDeletePresetModalOpen(false)
      displayToast('Preset deleted', 'success')
    }
  }

  return (
    <Filters className="analytics-filters">
      {/* NOTE: inside the fixed filters so that the pages content is offset by its height too */}
      <DeprecationBanner
        className={classNames('analytics-filters__deprecation-banner', { 'filters-collapsed': isFiltersCollapsed })}
      />
      {!isFiltersCollapsed ? (
        <FiltersContent>
          <div className="left-section">
            {state.displayedFilters.datePicker ? (
              <div className="period-container">
                <div className="date-pickers-container">
                  <DateInput
                    className="from-period"
                    endDate={state.filters.endDate?.[0]}
                    excludeToday
                    label="Date range selection"
                    maxDate={TODAY}
                    maxRangeDays={maxDateRange}
                    onDateRangeChange={handleDatePickerChanged}
                    startDate={state.filters.startDate?.[0]}
                    withFilters
                    withRange
                  />
                </div>
              </div>
            ) : null}
            {state.displayedFilters.dropdownDai ? (
              state.filters.kpi?.includes('dai') || state.filters.breakdown?.includes('dai') ? (
                <MultiSelectInput
                  className="filter-dropdown"
                  defaultIcon={<MdOutlineFlag size={16} />}
                  isLoading={isFiltersOptionsLoading}
                  label="DAI"
                  onChange={(value: unknown) => handleFilterChanged('dai', value as IOption[])}
                  options={daiOptions}
                  placeholder="DAI"
                  value={getOptionFromFilters(state.filters.dai || [], daiOptions)}
                  withOnly
                />
              ) : (
                <SelectInput
                  className="filter-dropdown"
                  isLoading={isFiltersOptionsLoading}
                  label="DAI"
                  onChange={(value: unknown) => handleFilterChanged('dai', value as IOption[])}
                  options={daiOptions}
                  placeholder="DAI"
                  value={getOptionFromFilters(state.filters.dai || [], daiOptions)}
                />
              )
            ) : null}
            {state.displayedFilters.dropdownInstallationAppVersion ? (
              <MultiSelectInput
                className="filter-dropdown"
                isLoading={isFiltersOptionsLoading}
                label="Installation App Version"
                onChange={(value: unknown) => handleFilterChanged('installationAppVersion', value as IOption[])}
                options={installationAppVersionOptions}
                placeholder="Installation App version"
                value={getOptionFromFilters(state.filters.installationAppVersion || [], installationAppVersionOptions)}
                withOnly
              />
            ) : null}
            {state.displayedFilters.dropdownInstallationCountry ? (
              <MultiSelectInput
                className="filter-dropdown"
                defaultIcon={<MdOutlineFlag size={16} />}
                filterOption={startsWithFilter}
                isLoading={isFiltersOptionsLoading}
                label="Installation Countries"
                onChange={(value: unknown) => handleFilterChanged('installationCountry', value as IOption[])}
                optionType="Countries"
                options={countryOptions}
                placeholder="All Installation Countries"
                value={getOptionFromFilters(state.filters.installationCountry || [], countryOptions)}
                withOnly
              />
            ) : null}
            {state.displayedFilters.dropdownCountry ? (
              <MultiSelectInput
                className="filter-dropdown"
                defaultIcon={<MdOutlineFlag size={16} />}
                filterOption={startsWithFilter}
                isLoading={isFiltersOptionsLoading}
                label="Countries"
                onChange={(value: unknown) => handleFilterChanged('country', value as IOption[])}
                optionType="Countries"
                options={countryOptions}
                placeholder="All Countries"
                value={getOptionFromFilters(state.filters.country || [], countryOptions)}
                withOnly
              />
            ) : null}
            {state.displayedFilters.dropdownAppKey ? (
              <MultiSelectInput
                components={{ Control: CustomAppControl, Option: CustomAppOption }}
                isClearable
                isLoading={isAppsLoading}
                isSearchable
                label="App key"
                onChange={(option) => handleFilterChanged('appKey', option as IOption[])}
                options={appKeyOptions}
                placeholder="All App Keys"
                value={getOptionFromFilters(state.filters.appKey || [], appKeyOptions)}
                withOnly
              />
            ) : null}
            {state.displayedFilters.dropdownDeviceCategory ? (
              <MultiSelectInput
                className="filter-dropdown"
                isLoading={isFiltersOptionsLoading}
                label="Device category"
                onChange={(value: unknown) => handleFilterChanged('deviceCategory', value as IOption[])}
                options={deviceCategoryOptions}
                placeholder="Device category"
                value={getOptionFromFilters(state.filters.deviceCategory || [], deviceCategoryOptions)}
                withOnly
              />
            ) : null}
            {state.displayedFilters.dropdownDeviceType ? (
              <MultiSelectInput
                className="filter-dropdown"
                isLoading={isFiltersOptionsLoading}
                label="Device type"
                onChange={(value: unknown) => handleFilterChanged('deviceType', value as IOption[])}
                options={deviceTypeOptions}
                placeholder="Device type"
                value={deviceTypeOptions.find(({ value }) => state.filters.deviceType?.includes(value))}
                withOnly
              />
            ) : null}
            {state.displayedFilters.dropdownUaSource ? (
              <MultiSelectInput
                className="filter-dropdown"
                isLoading={isFiltersOptionsLoading}
                label="UA source"
                onChange={(value: unknown) => handleFilterChanged('uaSource', value as IOption[])}
                options={uaSourceOptions}
                placeholder="UA source"
                value={getOptionFromFilters(state.filters.uaSource || [], uaSourceOptions)}
                withOnly
              />
            ) : null}
            {state.displayedFilters.dropdownUserType ? (
              <MultiSelectInput
                className="filter-dropdown"
                isLoading={isFiltersOptionsLoading}
                label="User type"
                onChange={(value: unknown) => handleFilterChanged('userType', value as IOption[])}
                options={userTypeOptions}
                placeholder="User type"
                value={getOptionFromFilters(state.filters.userType || [], userTypeOptions)}
                withOnly
              />
            ) : null}
            {state.displayedFilters.dropdownBreakdown ? (
              <SelectInput
                className="filter-dropdown"
                isClearable
                isLoading={isFiltersOptionsLoading}
                label="Breakdown"
                onChange={(value: unknown) => handleFilterChanged('breakdown', value as IOption[])}
                options={refinedBreakdownOptions}
                placeholder="Breakdown"
                value={getOptionFromFilters(state.filters.breakdown || [], refinedBreakdownOptions)}
              />
            ) : null}
            {state.filters.breakdown?.[0] === 'abtest' ? (
              <SelectInput
                className="filter-dropdown"
                isClearable
                isLoading={isFiltersOptionsLoading}
                label="abtest key"
                onChange={(value: unknown) => handleFilterChanged('abtestKey', value as IOption[])}
                options={abtestKeyOptions}
                placeholder="abtest_key"
                required
                value={getOptionFromFilters(state.filters.abtestKey || [], abtestKeyOptions)}
              />
            ) : null}
            {state.filters.breakdown?.[0] === 'dsv3' ? (
              <>
                <SelectInput
                  className="filter-dropdown"
                  isClearable
                  isLoading={isFiltersOptionsLoading}
                  label="Placement key"
                  menuClassName="placement-key-menu"
                  onChange={(value: unknown) => handleFilterChanged('placementKey', value as IOption[])}
                  options={placementKeyOptions}
                  placeholder="placement_key"
                  required
                  value={getOptionFromFilters(state.filters.placementKey || [], placementKeyOptions)}
                />
                <MultiSelectInput
                  className="filter-dropdown navigation-graph-id-select-input"
                  isLoading={isFiltersOptionsLoading}
                  label="Navigation Graph Id"
                  menuClassName="navigation-graph-id-select-input__menu"
                  onChange={(value: unknown) => handleFilterChanged('navigationGraphId', value as IOption[])}
                  options={navigationGraphIdOptions}
                  placeholder="navigation_graph_id"
                  value={getOptionFromFilters(state.filters.navigationGraphId || [], navigationGraphIdOptions)}
                  withOnly
                />
              </>
            ) : null}
          </div>
          <div className="right-section">
            {!location.pathname.includes('metrics-overview') && !state.filters.kpi?.includes('dai') ? (
              <Button ghost onClick={handleWithEventsToggleClick}>
                {withEvents ? 'Hide' : 'Show'} versions
              </Button>
            ) : null}
            <Button className="reset-button" ghost onClick={handleResetAll}>
              Reset all
            </Button>
            <div className="preset-button-container">
              <Button className="preset-button" ghost onClick={handleOpenPresets}>
                Filter presets
              </Button>
              <TooltipMenu isOpen={isPresetsOpen} ref={tooltipMenuRef}>
                <ul className="presets-menu-items">
                  {filtersPresets.length ? (
                    filtersPresets.map((preset) => (
                      <li className="presets-menu-item" key={preset.name} onClick={handleSelectPreset(preset)}>
                        {preset.name}
                        <div className="close-icon" onClick={handleOpenDeletePresetModal(true, preset)}>
                          &times;
                        </div>
                      </li>
                    ))
                  ) : (
                    <li className="presets-menu-item no-preset">No presets found</li>
                  )}
                  <li className="presets-menu-item create-new-preset" onClick={handleOpenPresetModal(true)}>
                    Create a preset
                  </li>
                </ul>
              </TooltipMenu>
            </div>
          </div>
        </FiltersContent>
      ) : null}
      <CollapseButton
        className="cohorted-analytics-filters__collapse-button"
        isCollapsed={isFiltersCollapsed}
        onClick={toggleCollapseFilters}
      />
      <NewPresetModal
        currentFilters={state.filters}
        isOpen={isPresetModalOpen}
        onClose={handleOpenPresetModal(false)}
      />
      <ModalDialog
        icon={<MdOutlineClear size={42} />}
        isOpen={isDeletePresetModalOpen}
        mainText="Delete preset"
        onClose={handleOpenDeletePresetModal(false)}
        onConfirm={handleDeletePreset}
        subText="Are you sure you want to delete this preset?"
      />
    </Filters>
  )
}

export default AnalyticsFilters
